import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { callAPI } from '../utils/apiUtils';
import { apiUrls } from '../utils/apiUrls';
import { toast } from 'react-toastify';
import {
  isOfflineAndroid,
  createRequestId,
  ensureCallbackRegistry,
} from '../utils/androidOfflineCart';


/**
 * Per-location feature toggles for this restaurant. The list itself lives on the server
 * (ConfigsController::POS_SETTINGS) - this page only renders whatever it is handed, so adding a
 * new toggle later is a backend-only change: one entry in that array, nothing here.
 */
const SettingsPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  // Draft text for a duration_seconds setting's "Custom" number input, keyed by setting.key -
  // kept separate from `settings` state so typing a custom value doesn't save on every keystroke;
  // it only commits on blur/Enter.
  const [customDrafts, setCustomDrafts] = useState({});

  const [printerSettings, setPrinterSettings] = useState({
    kitchenIp: '',
    kitchenPort: '9100',
    billingIp: '',
    billingPort: '9100',
  });

  const [printerSaving, setPrinterSaving] = useState(false);
  const [offlineSyncing, setOfflineSyncing] = useState(false);


const savePrinterSettings = (
  kitchenIp,
  kitchenPort,
  billingIp,
  billingPort
) => {

  return new Promise((resolve, reject) => {

    if (!isOfflineAndroid()) {
      reject(
        new Error(
          'Android POS offline bridge unavailable'
        )
      );
      return;
    }

    if (
      typeof window.AndroidPOS.savePrinterSettings !==
      'function'
    ) {
      reject(
        new Error(
          'AndroidPOS.savePrinterSettings is not available'
        )
      );
      return;
    }

    const requestId =
      createRequestId(
        'save_printer_settings'
      );

    ensureCallbackRegistry();

    window.__orderArtOfflineCallbacks[
      requestId
    ] = {
      resolve,
      reject
    };

    try {

      window.AndroidPOS.savePrinterSettings(
        requestId,
        JSON.stringify({

          kitchenIp,
          kitchenPort,

          billingIp,
          billingPort,

        })
      );

    } catch (error) {

      delete window
        .__orderArtOfflineCallbacks[
          requestId
        ];

      reject(error);

      return;
    }

    setTimeout(() => {

      if (
        window.__orderArtOfflineCallbacks[
          requestId
        ]
      ) {

        delete window
          .__orderArtOfflineCallbacks[
            requestId
          ];

        reject(
          new Error(
            `Save printer settings timeout: ${requestId}`
          )
        );
      }

    }, 10000);
  });
};

const getPrinterSettings = () => {
  return new Promise((resolve, reject) => {

    if (!isOfflineAndroid()) {
      reject(
        new Error(
          'Android POS offline bridge unavailable'
        )
      );
      return;
    }

    if (
      typeof window.AndroidPOS?.getPrinterSettings !== 'function'
    ) {
      reject(
        new Error(
          'AndroidPOS.getPrinterSettings is not available'
        )
      );
      return;
    }

    const requestId =
      createRequestId('get_printer_settings');

    ensureCallbackRegistry();

    window.__orderArtOfflineCallbacks[requestId] = {
      resolve,
      reject
    };

    try {

      window.AndroidPOS.getPrinterSettings(
        requestId
      );

    } catch (error) {

      delete window.__orderArtOfflineCallbacks[
        requestId
      ];

      reject(error);

      return;
    }

    setTimeout(() => {

      if (
        window.__orderArtOfflineCallbacks[requestId]
      ) {

        delete window.__orderArtOfflineCallbacks[
          requestId
        ];

        reject(
          new Error(
            `Get printer settings timeout: ${requestId}`
          )
        );
      }

    }, 10000);
  });
};


const syncOfflineData = async () => {
  try {
    setOfflineSyncing(true);

    // =========================================================
    // HELPER: SAVE THROUGH ANDROID BRIDGE
    // =========================================================

    const saveThroughAndroid = (
      method,
      payload,
      requestPrefix,
      timeoutMs = 30000
    ) => {
      return new Promise((resolve, reject) => {
        const requestId = createRequestId(requestPrefix);

        ensureCallbackRegistry();

        window.__orderArtOfflineCallbacks[requestId] = {
          resolve,
          reject
        };

        console.log(
          `[OFFLINE_SYNC] Calling ${method} requestId=${requestId}`
        );

        try {
          if (
            !window.AndroidPOS ||
            typeof window.AndroidPOS[method] !== 'function'
          ) {
            throw new Error(
              `AndroidPOS.${method} is not available`
            );
          }

          window.AndroidPOS[method](
            requestId,
            JSON.stringify(payload)
          );

        } catch (error) {
          delete window.__orderArtOfflineCallbacks[requestId];

          reject(error);

          return;
        }

        setTimeout(() => {
          if (
            window.__orderArtOfflineCallbacks[requestId]
          ) {
            delete window.__orderArtOfflineCallbacks[
              requestId
            ];

            reject(
              new Error(
                `${method} timeout: ${requestId}`
              )
            );
          }
        }, timeoutMs);
      });
    };

    // =========================================================
    // 1. FETCH CUSTOMERS
    // =========================================================

    console.log(
      '[OFFLINE_SYNC] ========================================'
    );

    console.log(
      '[OFFLINE_SYNC] STEP 1: Fetching customers'
    );

    const customerResponse = await callAPI(
      apiUrls.ALL_CUSTOMER_DATA,
      {},
      'GET'
    );

    console.log(
      '[OFFLINE_SYNC] RAW CUSTOMER RESPONSE:',
      customerResponse?.data?.data
    );

    const customerData =
      Array.isArray(customerResponse?.data?.data)
        ? customerResponse.data.data
        : [];

    const saveCustomers = customerData.map((customer) => {
      const {
        password,
        auth_key,
        password_reset_token,
        fbId,
        googleId,
        twitterId,
        ...saveCustomer
      } = customer;

      return saveCustomer;
    });

    const customerOfflinePayload = {
      status:
        customerResponse?.data?.status ?? 1,

      message:
        customerResponse?.data?.message ||
        'Customers',

      data: saveCustomers
    };

    console.log(
      '[OFFLINE_SYNC] Customers count:',
      saveCustomers.length
    );

    console.log(
      '[OFFLINE_SYNC] Customer payload length:',
      JSON.stringify(customerOfflinePayload).length
    );

    // =========================================================
    // 2. SAVE CUSTOMERS FIRST
    // =========================================================

    console.log(
      '[OFFLINE_SYNC] STEP 2: Saving customers FIRST'
    );

    const customerResult =
      await saveThroughAndroid(
        'saveOfflineCustomers',
        customerOfflinePayload,
        'save_offline_customers',

        /*
         * Customer payload is large.
         *
         * Your current log shows:
         *
         * payload = ~15.9 MB
         * Room save = ~10.7 seconds
         *
         * Give it 60 seconds.
         */
        60000
      );

    console.log(
      '[OFFLINE_SYNC] Customers saved successfully:',
      customerResult
    );

    // =========================================================
    // IMPORTANT:
    //
    // DO NOT FETCH/SAVE DELIVERY ADDRESSES BEFORE THIS POINT.
    //
    // Delivery addresses need CustomerEntity.localCustomerId.
    //
    // =========================================================

    // =========================================================
    // 3. FETCH SERVERS + DELIVERY ADDRESSES
    //
    // They can be fetched in parallel because both are
    // independent HTTP APIs.
    // =========================================================

    console.log(
      '[OFFLINE_SYNC] STEP 3: Fetching servers + delivery addresses'
    );

    const [
      serverResponse,
      deliveryAddressResponse
    ] = await Promise.all([
      callAPI(
        apiUrls.SERVER_DATA,
        {},
        'GET'
      ),

      callAPI(
        apiUrls.ALL_DELIVERY_ADDRESS,
        {},
        'GET'
      )
    ]);

    // =========================================================
    // SERVERS PAYLOAD
    // =========================================================

    const servers =
      Array.isArray(
        serverResponse?.data?.data?.servers
      )
        ? serverResponse.data.data.servers
        : [];

    const serverOfflinePayload = {
      status:
        serverResponse?.data?.status ?? 1,

      message:
        serverResponse?.data?.message ||
        'Servers',

      data: {
        servers
      }
    };

    console.log(
      '[OFFLINE_SYNC] Servers count:',
      servers.length
    );

    // =========================================================
    // DELIVERY ADDRESS PAYLOAD
    // =========================================================

    console.log(
      '[OFFLINE_SYNC] RAW DELIVERY ADDRESS RESPONSE:',
      JSON.stringify(
        deliveryAddressResponse?.data,
        null,
        2
      )
    );

    const deliveryAddresses =
      Array.isArray(
        deliveryAddressResponse?.data?.data
      )
        ? deliveryAddressResponse.data.data
        : [];

    const deliveryAddressOfflinePayload = {
      status:
        deliveryAddressResponse?.data?.status ?? 1,

      message:
        deliveryAddressResponse?.data?.message ||
        'Delivery Addresses',

      data: deliveryAddresses
    };

    console.log(
      '[OFFLINE_SYNC] Delivery addresses count:',
      deliveryAddresses.length
    );

    console.log(
      '[OFFLINE_SYNC] Delivery address payload length:',
      JSON.stringify(
        deliveryAddressOfflinePayload
      ).length
    );

    // =========================================================
    // 4. SAVE SERVERS + DELIVERY ADDRESSES
    //
    // NOW customers already exist in Room.
    //
    // DeliveryAddressRepository can safely do:
    //
    // serverUserId
    //      ↓
    // CustomerEntity.serverCustomerId
    //      ↓
    // CustomerEntity.localCustomerId
    //
    // =========================================================

    console.log(
      '[OFFLINE_SYNC] STEP 4: Saving servers + delivery addresses'
    );

    const [
      serverResult,
      deliveryAddressResult
    ] = await Promise.all([
      saveThroughAndroid(
        'saveOfflineServers',
        serverOfflinePayload,
        'save_offline_servers',
        30000
      ),

      saveThroughAndroid(
        'saveOfflineDeliveryAddresses',
        deliveryAddressOfflinePayload,
        'save_offline_delivery_addresses',
        30000
      )
    ]);

    // =========================================================
    // 5. SUCCESS
    // =========================================================

    console.log(
      '[OFFLINE_SYNC] Customers saved:',
      customerResult
    );

    console.log(
      '[OFFLINE_SYNC] Servers saved:',
      serverResult
    );

    console.log(
      '[OFFLINE_SYNC] Delivery addresses saved:',
      deliveryAddressResult
    );

    console.log(
      '[OFFLINE_SYNC] ========================================'
    );

    console.log(
      '[OFFLINE_SYNC] COMPLETE'
    );

    toast.success(
      `Offline data synced: ${saveCustomers.length} customers, ${servers.length} servers, ${deliveryAddresses.length} delivery addresses`
    );

  } catch (error) {

    console.error(
      '[OFFLINE_SYNC] Failed:',
      error
    );

    toast.error(
      error?.message ||
      'Failed to sync offline data'
    );

  } finally {

    setOfflineSyncing(false);
  }
};




  useEffect(() => {
    // const token = localStorage.getItem('accessToken');
    // if (!token) {
    //   navigate('/');
    //   return;
    // }

    (async () => {
      try {
        const res = await callAPI(apiUrls.POS_SETTINGS_GET, {}, 'GET');
        setSettings(res?.data?.data || []);
        if (isOfflineAndroid()) {

              try {

                const rawResult =  await getPrinterSettings();

                const result =
                  typeof rawResult === 'string'
                    ? JSON.parse(rawResult)
                    : rawResult;


                    if (result?.success) {

                      setPrinterSettings({

                        kitchenIp:
                          result.kitchenIp || '',

                        kitchenPort:
                          String(
                            result.kitchenPort || 9100
                          ),

                        billingIp:
                          result.billingIp || '',

                        billingPort:
                          String(
                            result.billingPort || 9100
                          ),

                      });
                    }

              } catch (printerError) {

                console.log(
                  'Printer settings unavailable:',
                  printerError
                );

              }
          }
      } catch (e) {
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const toggle = async (key, nextValue) => {
    // Optimistic - a manager flipping this switch should see it move immediately, not wait on a
    // round trip. Reverted below if the save fails.
    setSettings((prev) => prev.map((s) => (s.key === key ? { ...s, value: nextValue } : s)));
    setSavingKey(key);
    try {
      await callAPI(apiUrls.POS_SETTINGS_UPDATE, {}, 'POST', { [key]: nextValue ? 1 : 0 }, {});
    } catch (e) {
      setSettings((prev) => prev.map((s) => (s.key === key ? { ...s, value: !nextValue } : s)));
      toast.error('Could not save that setting. Please try again.');
    } finally {
      setSavingKey(null);
    }
  };

  const saveDuration = async (key, prevValue, seconds) => {
    if (!Number.isFinite(seconds) || seconds < 1) {
      toast.error('Enter a whole number of seconds greater than 0.');
      return;
    }
    setSettings((prev) => prev.map((s) => (s.key === key ? { ...s, value: seconds } : s)));
    setSavingKey(key);
    try {
      await callAPI(apiUrls.POS_SETTINGS_UPDATE, {}, 'POST', { [key]: seconds }, {});
    } catch (e) {
      setSettings((prev) => prev.map((s) => (s.key === key ? { ...s, value: prevValue } : s)));
      toast.error('Could not save that setting. Please try again.');
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <MainLayout>
      <div className="pageContent">
        <div className="container">
          <h2 className="lgTitle mb-3">Settings</h2>
            <div className="cardStyle mb-4">
              <div className="cardBody mb-4">
                  <h5 className="mb-1">
                    Printer Settings
                  </h5>

                  <p
                    className="mb-0"
                    style={{
                      opacity: 0.7,
                      fontSize: 13
                    }}
                  >
                    Save both kitchen and billing printer settings to this POS.
                  </p>
              </div>

              <div className="cardBody mb-4">

                <h5 className="mb-1">
                  Kitchen Printer
                </h5>

                <p
                  className="mb-3"
                  style={{
                    opacity: 0.7,
                    fontSize: 13
                  }}
                >
                  Used for kitchen order dockets.
                </p>

                <div className="row g-3">

                  <div className="col-md-7">

                    <label className="form-label">
                      Printer IP Address
                    </label>

                    <input
                      type="text"
                      className="form-control"
                      placeholder="192.168.29.114"
                      value={printerSettings.kitchenIp}
                      onChange={(e) =>
                        setPrinterSettings((prev) => ({
                          ...prev,
                          kitchenIp: e.target.value
                        }))
                      }
                    />

                  </div>

                  <div className="col-md-5">

                    <label className="form-label">
                      Port
                    </label>

                    <input
                      type="number"
                      min="1"
                      max="65535"
                      className="form-control"
                      value={
                        printerSettings.kitchenPort
                      }
                      onChange={(e) =>
                        setPrinterSettings((prev) => ({
                          ...prev,
                          kitchenPort: e.target.value
                        }))
                      }
                    />

                  </div>

                </div>

              </div>
            

              
                <div className="cardBody mb-4">

                  <h5 className="mb-1">
                    Billing Printer
                  </h5>

                  <p
                    className="mb-3"
                    style={{
                      opacity: 0.7,
                      fontSize: 13
                    }}
                  >
                    Used for customer bills and receipts.
                  </p>

                  <div className="row g-3">

                    <div className="col-md-7">

                      <label className="form-label">
                        Printer IP Address
                      </label>

                      <input
                        type="text"
                        className="form-control"
                        placeholder="192.168.29.115"
                        value={printerSettings.billingIp}
                        onChange={(e) =>
                          setPrinterSettings((prev) => ({
                            ...prev,
                            billingIp: e.target.value
                          }))
                        }
                      />

                    </div>

                    <div className="col-md-5">

                      <label className="form-label">
                        Port
                      </label>

                      <input
                        type="number"
                        min="1"
                        max="65535"
                        className="form-control"
                        value={
                          printerSettings.billingPort
                        }
                        onChange={(e) =>
                          setPrinterSettings((prev) => ({
                            ...prev,
                            billingPort: e.target.value
                          }))
                        }
                      />

                    </div>

                  </div>

                </div>


                
  <div className="cardBody">

    <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">



      <button
        type="button"
        className="btn btn-primary"
        disabled={printerSaving}
        onClick={async () => {

          const kitchenIp =
            printerSettings.kitchenIp.trim();

          const kitchenPort =
            parseInt(
              printerSettings.kitchenPort,
              10
            );

          const billingIp =
            printerSettings.billingIp.trim();

          const billingPort =
            parseInt(
              printerSettings.billingPort,
              10
            );

          // ==========================================
          // VALIDATE KITCHEN PRINTER
          // ==========================================

          if (!kitchenIp) {
            toast.error(
              'Enter kitchen printer IP address.'
            );
            return;
          }

          if (
            !Number.isInteger(kitchenPort) ||
            kitchenPort < 1 ||
            kitchenPort > 65535
          ) {
            toast.error(
              'Enter a valid kitchen printer port.'
            );
            return;
          }

          // ==========================================
          // VALIDATE BILLING PRINTER
          // ==========================================

          if (!billingIp) {
            toast.error(
              'Enter billing printer IP address.'
            );
            return;
          }

          if (
            !Number.isInteger(billingPort) ||
            billingPort < 1 ||
            billingPort > 65535
          ) {
            toast.error(
              'Enter a valid billing printer port.'
            );
            return;
          }

          try {

            setPrinterSaving(true);

            // ========================================
            // SAVE BOTH PRINTERS
            // ========================================

            const rawResult =
              await savePrinterSettings(
                kitchenIp,
                kitchenPort,
                billingIp,
                billingPort
              );

            const result =
              typeof rawResult === 'string'
                ? JSON.parse(rawResult)
                : rawResult;

            if (!result?.success) {
              throw new Error(
                result?.error ||
                'Failed to save printer settings'
              );
            }

            // ========================================
            // UPDATE UI WITH SAVED VALUES
            // ========================================

            setPrinterSettings({

              kitchenIp:
                result.kitchenIp ||
                kitchenIp,

              kitchenPort:
                String(
                  result.kitchenPort ||
                  kitchenPort
                ),

              billingIp:
                result.billingIp ||
                billingIp,

              billingPort:
                String(
                  result.billingPort ||
                  billingPort
                )

            });

            toast.success(
              'Kitchen and billing printer settings saved'
            );

          } catch (error) {

            console.error(
              'Failed saving printer settings:',
              error
            );

            toast.error(
              error?.message ||
              'Could not save printer settings'
            );

          } finally {

            setPrinterSaving(false);

          }
        }}
      >
        {printerSaving
          ? 'Saving...'
          : 'Save Printer Settings'}
      </button>

    </div>

  </div>


              </div>



      <div className="cardStyle mb-4">
        <div className="cardBody">

          <h5 className="mb-1">
            Offline Data
          </h5>

          <p
            className="mb-3"
            style={{
              opacity: 0.7,
              fontSize: 13
            }}
          >
            Download customers, delivery address and servers to this POS so they
            are available when the POS is offline.
          </p>

          <button
            type="button"
            className="btn btn-primary"
            disabled={offlineSyncing}
            onClick={syncOfflineData}
          >
            {offlineSyncing
              ? 'Syncing...'
              : 'Sync Offline Data'}
          </button>

        </div>
      </div>



          {loading ? (
            <p>Loading…</p>
          ) : settings.length === 0 ? (
            <div className="cardStyle mb-4">
              <div className="cardBody">No configurable features yet.</div>
            </div>
          ) : (
            settings.map((setting) => {
              if (setting.type === 'duration_seconds') {
                // Only meaningful once its sibling enable/disable toggle is on, if one is
                // present in the list - grey it out rather than hide it, so it's clear the
                // control exists but isn't currently in effect.
                const gateSetting = settings.find((s) => s.key === 'staff_lock_screen_enabled');
                const isGated = !!gateSetting && !gateSetting.value;
                // Showing the custom input is "the saved value isn't a preset" OR "the user just
                // picked Custom from the dropdown" - the latter has no saved value to derive from
                // yet, hence the separate draft-presence check.
                const hasDraft = customDrafts[setting.key] !== undefined;
                const isCustom = hasDraft || !setting.options.includes(setting.value);
                const draft = customDrafts[setting.key] ?? String(setting.value);
                const commitCustom = () => {
                  const seconds = parseInt(draft, 10);
                  saveDuration(setting.key, setting.value, seconds);
                };

                return (

                  
                  <div className="cardStyle mb-4" key={setting.key} style={isGated ? { opacity: 0.5 } : undefined}>
                    <div className="cardBody d-flex align-items-start justify-content-between gap-3 flex-wrap">
                      <div>
                        <h5 className="mb-1">{setting.title}</h5>
                        {setting.subtitle && (
                          <p className="mb-0" style={{ opacity: 0.7, fontSize: 13 }}>
                            {setting.subtitle}
                          </p>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-2 flex-shrink-0">
                        <select
                          className="form-select"
                          style={{ width: 140 }}
                          disabled={isGated || savingKey === setting.key}
                          value={isCustom ? 'custom' : setting.value}
                          onChange={(e) => {
                            if (e.target.value === 'custom') {
                              setCustomDrafts((prev) => ({ ...prev, [setting.key]: String(setting.value) }));
                              return;
                            }
                            setCustomDrafts((prev) => {
                              const next = { ...prev };
                              delete next[setting.key];
                              return next;
                            });
                            saveDuration(setting.key, setting.value, parseInt(e.target.value, 10));
                          }}
                        >
                          {setting.options.map((seconds) => (
                            <option key={seconds} value={seconds}>
                              {seconds} seconds
                            </option>
                          ))}
                          <option value="custom">Custom</option>
                        </select>
                        {isCustom && (
                          <input
                            type="number"
                            min="1"
                            className="form-control"
                            style={{ width: 100 }}
                            disabled={isGated || savingKey === setting.key}
                            value={draft}
                            onChange={(e) => setCustomDrafts((prev) => ({ ...prev, [setting.key]: e.target.value }))}
                            onBlur={commitCustom}
                            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div className="cardStyle mb-4" key={setting.key}>
                  <div className="cardBody d-flex align-items-start justify-content-between gap-3">
                    <div>
                      <h5 className="mb-1">{setting.title}</h5>
                      {setting.subtitle && (
                        <p className="mb-0" style={{ opacity: 0.7, fontSize: 13 }}>
                          {setting.subtitle}
                        </p>
                      )}
                    </div>
                    <div className="form-check form-switch mb-0 flex-shrink-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={!!setting.value}
                        disabled={savingKey === setting.key}
                        onChange={(e) => toggle(setting.key, e.target.checked)}
                      />
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default SettingsPage;
